// Copyright The Linux Foundation and each contributor to LFX.
// SPDX-License-Identifier: MIT
import { AlertService } from '../../../core/services/alert.service';
import { Component, OnInit, OnDestroy } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { Store, select } from '@ngrx/store';
import { Observable, BehaviorSubject, Subscription } from 'rxjs';
import { map, switchMap, take, filter, tap } from 'rxjs/operators';
import {
  Role,
  RoleDetail,
  RoleUserSelections,
  readParameterFromRouterState,
  RolesService,
  GoAction,
  QueueAlertAction,
  AlertType,
  IAM_ROUTE,
  IAMUser
} from '@app/core';
import {
  State,
  LoadingRoleDetailsAction,
  ClearRoleUserSelectionsAction,
  selectRoleDetail,
  selectRoleUserSelections,
  selectUserToEdit,
  SelectUsersForRoleAction
} from '../../state';

const shouldLoadRole = (roleDetail: RoleDetail | undefined) =>
  roleDetail === undefined || roleDetail.role === undefined;

@Component({
  selector: 'app-manage-user-scopes',
  templateUrl: './manage-user-scopes.component.html',
  styleUrls: ['./manage-user-scopes.component.scss']
})
export class ManageUserScopesComponent implements OnInit, OnDestroy {
  roleId: string;
  readonly role$: Observable<Role | undefined>;
  readonly user$: Observable<IAMUser | undefined>;
  readonly roleDetail$: Observable<RoleDetail | undefined>;

  readonly isSubmitting$ = new BehaviorSubject<boolean>(false);
  readonly failedSubmit$ = new BehaviorSubject<boolean>(false);
  isValid = false;

  addUserScopeMode = false;

  useProjectCommitteeHandler = false;
  private submitSubscription: Subscription | undefined = undefined;
  _selectedUser: any;

  constructor(
    private activatedRoute: ActivatedRoute,
    private store: Store<State>,
    private alertService: AlertService,
    private rolesService: RolesService
  ) {
    this.roleId = this.getRoleId();

    const maybeRoleDetail$ = this.selectRoleDetail$();
    this.loadRoleDetailsIfNecessary(maybeRoleDetail$);
    this.role$ = this.getRole$(maybeRoleDetail$);
    this.roleDetail$ = maybeRoleDetail$;

    this.user$ = this.store.pipe(select(selectUserToEdit(this.roleId)));
    this.user$.pipe(take(1)).subscribe(user => {
      this._selectedUser = user;
    });
  }

  ngOnInit() {}

  onOptionSelected(option: string) {
    this.useProjectCommitteeHandler = option === 'project|committee';
  }

  onSaved() {
    this.addUserScopeMode = false;
    this.useProjectCommitteeHandler = false;
    this._selectedUser = {
      ...this._selectedUser
    };
  }

  ngOnDestroy() {
    if (this.submitSubscription) {
      this.submitSubscription.unsubscribe();
    }
  }

  addUserScope() {
    this.addUserScopeMode = true;
  }

  updateValidity(isValid: boolean) {
    this.isValid = isValid;
  }

  submit() {
    if (!this.isValid) {
      return;
    }

    const submit$ = this.store.pipe(
      select(selectRoleUserSelections(this.roleId)),
      switchMap((roleUserSelections: RoleUserSelections) => {
        const request = roleUserSelections;
        if (!request.users || !request.users.length) {
          request.users = [this._selectedUser];
        }

        return this.rolesService.updateUserScope(this.roleId, roleUserSelections);
      })
    );
    this.submitSubscription = submit$.subscribe(
      _ => {
        this.reloadRoleDetail();
        this.alertService.success(`Successfully updated user.`);
      },
      err => {
        this.alertService.error('Failed to assign user to role.');
      }
    );
  }

  reloadRoleDetail() {
    this.store.dispatch(new LoadingRoleDetailsAction(this.roleId));
    this.store.dispatch(new SelectUsersForRoleAction({ roleId: this.roleId, selectedUsers: [this._selectedUser] }));
    this.addUserScopeMode = false;
  }

  cancel() {
    this.addUserScopeMode = false;
  }

  private goToRoleDetailsPage() {
    this.store.dispatch(new GoAction(['/', IAM_ROUTE, 'roles', this.roleId]));
  }

  private setSubmitting() {
    this.isSubmitting$.next(true);
    this.failedSubmit$.next(false);
  }

  private failSubmitting() {
    this.isSubmitting$.next(false);
    this.failedSubmit$.next(true);
  }

  private getRoleId() {
    const roleId = readParameterFromRouterState(this.activatedRoute, 'id');
    if (roleId === undefined) {
      throw new Error('role id not in route');
    }
    return roleId;
  }

  private selectRoleDetail$(): Observable<RoleDetail | undefined> {
    return this.store.pipe(select(selectRoleDetail(this.roleId)));
  }

  private getRole$(maybeRoleDetail$: Observable<RoleDetail | undefined>) {
    const roleDetail$: Observable<RoleDetail> = maybeRoleDetail$.pipe(
      filter<RoleDetail>(maybeRoleDetail => maybeRoleDetail !== undefined)
    );
    return roleDetail$.pipe(map(roleDetail => roleDetail.role));
  }

  private loadRoleDetailsIfNecessary(maybeRoleDetail$: Observable<RoleDetail | undefined>) {
    return maybeRoleDetail$
      .pipe(take(1), filter(shouldLoadRole))
      .subscribe(() => this.store.dispatch(new LoadingRoleDetailsAction(this.roleId)));
  }
}



// WEBPACK FOOTER //
// ./src/app/access-management/roles/manage-user-scopes/manage-user-scopes.component.ts